import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #intersectionObserver = null;
  #offscreen = false;
  #lastScrollTop = 0;
  #timeout = null;
  #scrollRafId = null;
  #delayedStickyActive = false;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  // ✅ UPDATED OBSERVER (supports delayed)
  #observeStickyPosition = (mode) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      if (mode === 'always') {
        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
      }

      // ✅ NEW: delayed mode
      if (mode === 'delayed') {
        this.#delayedStickyActive = !entry.isIntersecting;
        this.dataset.stickyState = this.#delayedStickyActive ? 'active' : 'inactive';
      }

      if (this.dataset.themeColor) {
        changeMetaThemeColor(this.dataset.themeColor);
      }
    });

    if (mode === 'delayed') {
      const trigger = document.querySelector('#sticky-trigger');

      if (trigger) {
        this.#intersectionObserver.observe(trigger);
      } else {
        // fallback: simulate first fold
        this.#intersectionObserver.observe(this);
      }
    } else {
      this.#intersectionObserver.observe(this);
    }
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    // ✅ DELAYED FALLBACK (if no observer trigger)
    if (stickyMode === 'delayed' && !this.#intersectionObserver) {
      const triggerPoint = window.innerHeight;

      this.dataset.stickyState = scrollTop > triggerPoint ? 'active' : 'inactive';
      return;
    }

    if (!this.#offscreen && stickyMode !== 'always') return;

    if (Math.abs(scrollTop - this.#lastScrollTop) < 5) return;

    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    // ✅ ALWAYS MODE (unchanged)
    if (stickyMode === 'always') {
      if (scrollTop <= 5) {
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // ✅ SCROLL-UP MODE (unchanged)
    if (stickyMode === 'scroll-up') {
      if (isScrollingUp) {
        if (scrollTop <= 0) {
          this.#offscreen = false;
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
        }
      } else if (this.dataset.stickyState === 'active') {
        this.dataset.scrollDirection = 'none';
        this.dataset.stickyState = 'idle';
      } else {
        this.dataset.scrollDirection = 'none';
        this.dataset.stickyState = 'idle';
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      // ✅ UPDATED: pass mode
      this.#observeStickyPosition(stickyMode);

      if (
        stickyMode === 'scroll-up' ||
        stickyMode === 'always' ||
        stickyMode === 'delayed'
      ) {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);

      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver(() => {
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (element instanceof HTMLElement) {
          resizeObserver.observe(element);
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});